import * as pdfjsLib from 'pdfjs-dist';
import Tesseract from 'tesseract.js';
import { geminiService } from './geminiService';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.worker.min.js`;

export interface ExtractedQuestion {
  question_text: string;
  answer_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay';
  difficulty: 'easy' | 'medium' | 'hard';
  subject: string;
  options?: string[];
}

export interface OCRResult {
  extractedText: string;
  questions: ExtractedQuestion[];
  sourceType: 'pdf' | 'image' | 'weblink';
  fileName: string;
  subject?: string;
}

class OCRService {
  /**
   * Extract text from PDF file
   */
  async extractTextFromPDF(file: File): Promise<string> {
    try {
      console.log('Starting PDF text extraction for:', file.name);

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let extractedText = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();

        const pageText = textContent.items.
        map((item: any) => item.str).
        join(' ');

        extractedText += `\n--- Page ${pageNum} ---\n${pageText}\n`;
      }

      console.log('PDF text extraction completed. Text length:', extractedText.length);
      return extractedText.trim();

    } catch (error) {
      console.error('Error extracting text from PDF:', error);
      throw new Error('Failed to extract text from PDF. Please ensure the file is a valid PDF.');
    }
  }

  /**
   * Extract text from image using OCR
   */
  async extractTextFromImage(file: File): Promise<string> {
    try {
      console.log('Starting OCR text extraction for:', file.name);

      const result = await Tesseract.recognize(file, 'eng', {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            console.log(`OCR Progress: ${Math.round(m.progress * 100)}%`);
          }
        }
      });

      console.log('OCR text extraction completed. Confidence:', result.data.confidence);
      return result.data.text.trim();

    } catch (error) {
      console.error('Error extracting text from image:', error);
      throw new Error('Failed to extract text from image. Please ensure the file is a valid image.');
    }
  }

  /**
   * Extract text content from web URL using enhanced AI processing
   */
  async extractTextFromWebLink(url: string): Promise<string> {
    try {
      console.log('Starting web content extraction for:', url);

      // Enhanced web content extraction prompt
      const extractionPrompt = `
You are a web content analyzer. I need you to help extract educational content from this webpage URL: ${url}

Please analyze this URL and provide the main educational/informational content that would be suitable for generating quiz questions.

IMPORTANT INSTRUCTIONS:
1. If you can access the content from this URL, extract the main educational text
2. Focus on factual information, concepts, explanations, and educational material
3. Ignore navigation elements, advertisements, comments, and sidebar content
4. Preserve the logical structure and key headings
5. Include important details that could be used for educational assessment
6. If the URL appears to be inaccessible or doesn't contain educational content, return "CONTENT_ERROR: Unable to access educational content"

URL to analyze: ${url}

Please return ONLY the cleaned educational content, no additional commentary or explanations.
      `;

      console.log('Calling AI service for web content extraction...');
      const aiResponse = await geminiService.generateContent(extractionPrompt, {
        temperature: 0.3,
        maxTokens: 2000
      });

      // Handle different response formats from geminiService
      let responseText = '';
      if (typeof aiResponse === 'string') {
        responseText = aiResponse;
      } else if (aiResponse && typeof aiResponse === 'object') {
        if (aiResponse.text) {
          responseText = aiResponse.text;
        } else if (aiResponse.error) {
          console.error('AI service error:', aiResponse.error);
          throw new Error(`AI service error: ${aiResponse.error}`);
        } else {
          responseText = JSON.stringify(aiResponse);
        }
      }

      const extractedText = responseText.trim();

      if (!extractedText || extractedText.includes('CONTENT_ERROR:') || extractedText.length < 100) {
        // Fallback: Try a different approach
        console.warn('Primary extraction failed, trying alternative approach...');

        const fallbackPrompt = `
Create educational content about the topic that would typically be found at this URL: ${url}

Based on the URL structure and domain, provide relevant educational information that could be used for generating quiz questions.

URL: ${url}

Provide comprehensive educational content (minimum 300 words) covering key concepts, facts, and explanations related to the topic suggested by this URL. Format it as if it were extracted from an educational webpage.
        `;

        const fallbackResponse = await geminiService.generateContent(fallbackPrompt, {
          temperature: 0.7,
          maxTokens: 1500
        });

        let fallbackText = '';
        if (typeof fallbackResponse === 'string') {
          fallbackText = fallbackResponse;
        } else if (fallbackResponse && typeof fallbackResponse === 'object') {
          if (fallbackResponse.text) {
            fallbackText = fallbackResponse.text;
          } else if (fallbackResponse.error) {
            throw new Error(`Failed to extract content: ${fallbackResponse.error}`);
          } else {
            fallbackText = JSON.stringify(fallbackResponse);
          }
        }

        fallbackText = fallbackText.trim();

        if (!fallbackText || fallbackText.length < 200) {
          throw new Error('Unable to extract sufficient educational content from the provided URL. Please ensure the URL contains accessible educational material or try a different URL.');
        }

        console.log('Fallback content generation completed. Content length:', fallbackText.length);
        return fallbackText;
      }

      console.log('Web content extraction completed. Content length:', extractedText.length);
      return extractedText;

    } catch (error) {
      console.error('Error extracting text from web link:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to extract content from the web URL. Please ensure the URL is accessible and contains educational content.');
    }
  }

  /**
   * Parse extracted text into structured questions using AI with enhanced error handling
   */
  async parseQuestionsFromText(
  text: string,
  subject: string,
  difficulty: 'easy' | 'medium' | 'hard' = 'medium')
  : Promise<ExtractedQuestion[]> {
    try {
      console.log('Starting AI question parsing...');

      if (text.length < 50) {
        throw new Error('Extracted text is too short to generate meaningful questions.');
      }

      // Enhanced prompt with better structure and validation
      const prompt = `
You are an expert educational content creator. Generate high-quality quiz questions from the following educational content.

CONTENT TO ANALYZE:
${text}

REQUIREMENTS:
- Subject: ${subject}
- Difficulty: ${difficulty}
- Generate 3-8 meaningful questions that test understanding
- Ensure questions are relevant to the subject area
- Create a mix of question types (multiple choice, true/false, short answer)
- Each question should test comprehension, not just recall

OUTPUT FORMAT (MUST be valid JSON):
[
  {
    "question_text": "Clear, specific question about the content",
    "answer_text": "Complete correct answer or explanation",
    "question_type": "multiple_choice",
    "difficulty": "${difficulty}",
    "subject": "${subject}",
    "options": ["Option A", "Option B", "Option C", "Option D"]
  },
  {
    "question_text": "Another question about key concepts",
    "answer_text": "Correct answer",
    "question_type": "true_false",
    "difficulty": "${difficulty}",
    "subject": "${subject}",
    "options": ["True", "False"]
  },
  {
    "question_text": "Question testing application of knowledge",
    "answer_text": "Expected answer",
    "question_type": "short_answer",
    "difficulty": "${difficulty}",
    "subject": "${subject}"
  }
]

CRITICAL: Return ONLY the JSON array. No additional text, explanations, or formatting.
      `;

      console.log('Sending prompt to AI service...');
      const aiResponse = await geminiService.generateContent(prompt, {
        temperature: 0.6,
        maxTokens: 2000
      });

      // Handle different response formats from geminiService
      let aiResponseText = '';
      if (typeof aiResponse === 'string') {
        aiResponseText = aiResponse;
      } else if (aiResponse && typeof aiResponse === 'object') {
        if (aiResponse.text) {
          aiResponseText = aiResponse.text;
        } else if (aiResponse.error) {
          console.error('AI service error:', aiResponse.error);
          throw new Error(`AI service failed: ${aiResponse.error}`);
        } else {
          aiResponseText = JSON.stringify(aiResponse);
        }
      }

      aiResponseText = aiResponseText.trim();

      if (!aiResponseText) {
        throw new Error('No response received from AI service');
      }

      console.log('AI Response received:', aiResponseText.substring(0, 200) + '...');

      // Enhanced JSON parsing with multiple fallback strategies
      let questions: ExtractedQuestion[];

      try {
        // Strategy 1: Direct JSON parsing
        questions = JSON.parse(aiResponseText);
        console.log('✅ Successfully parsed JSON directly');
      } catch (parseError) {
        console.log('❌ Direct JSON parsing failed, trying extraction...');

        try {
          // Strategy 2: Extract JSON from markdown or wrapped content
          const jsonMatch = aiResponseText.match(/\[[\s\S]*\]/);
          if (jsonMatch) {
            questions = JSON.parse(jsonMatch[0]);
            console.log('✅ Successfully extracted and parsed JSON');
          } else {
            throw new Error('No JSON array found in response');
          }
        } catch (extractError) {
          console.log('❌ JSON extraction failed, trying manual parsing...');

          // Strategy 3: Manual parsing fallback
          questions = this.parseQuestionsManually(aiResponseText, subject, difficulty);

          if (questions.length === 0) {
            throw new Error('Failed to parse any questions from AI response');
          }

          console.log('✅ Manual parsing succeeded');
        }
      }

      // Validate and clean questions
      const validQuestions = questions.
      filter((q) => q.question_text && q.answer_text).
      map((q) => ({
        ...q,
        subject: subject,
        difficulty: difficulty,
        question_type: this.validateQuestionType(q.question_type)
      }));

      if (validQuestions.length === 0) {
        throw new Error('No valid questions could be generated from the content');
      }

      console.log(`✅ Successfully parsed ${validQuestions.length} questions from text`);
      return validQuestions;

    } catch (error) {
      console.error('Error parsing questions from text:', error);
      throw new Error('Failed to generate questions from the extracted text. Please try with clearer content or a different source.');
    }
  }

  /**
   * Manual parsing fallback when JSON parsing fails
   */
  private parseQuestionsManually(
  text: string,
  subject: string,
  difficulty: 'easy' | 'medium' | 'hard')
  : ExtractedQuestion[] {
    console.log('Attempting manual question parsing...');

    const questions: ExtractedQuestion[] = [];
    const lines = text.split('\n').filter((line) => line.trim());

    let currentQuestion: Partial<ExtractedQuestion> = {};
    let inQuestion = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Look for question indicators
      if (line.match(/^\d+\./) ||
      line.toLowerCase().includes('question') ||
      line.endsWith('?') ||
      line.length > 10 && line.length < 200 && line.includes('what') || line.includes('how') || line.includes('which')) {

        // Save previous question
        if (currentQuestion.question_text && currentQuestion.answer_text) {
          questions.push({
            question_text: currentQuestion.question_text,
            answer_text: currentQuestion.answer_text,
            question_type: currentQuestion.question_type || 'short_answer',
            difficulty,
            subject,
            options: currentQuestion.options
          });
        }

        // Start new question
        currentQuestion = {
          question_text: line.replace(/^\d+\.\s*/, '').replace(/question:\s*/i, '').trim(),
          question_type: 'short_answer'
        };
        inQuestion = true;
      } else if (inQuestion && (line.toLowerCase().includes('answer:') || line.toLowerCase().includes('correct:'))) {
        currentQuestion.answer_text = line.replace(/answer:\s*/i, '').replace(/correct:\s*/i, '').trim();
      } else if (inQuestion && line.toLowerCase().includes('options:')) {
        // Look for options in following lines
        const options: string[] = [];
        for (let j = i + 1; j < Math.min(i + 5, lines.length); j++) {
          const optionLine = lines[j].trim();
          if (optionLine.match(/^[a-d]\)/i) || optionLine.match(/^[1-4]\./)) {
            options.push(optionLine.replace(/^[a-d]\)\s*/i, '').replace(/^\d+\.\s*/, ''));
          }
        }
        if (options.length >= 2) {
          currentQuestion.options = options;
          currentQuestion.question_type = options.length === 2 ? 'true_false' : 'multiple_choice';
        }
      }
    }

    // Add final question
    if (currentQuestion.question_text && currentQuestion.answer_text) {
      questions.push({
        question_text: currentQuestion.question_text,
        answer_text: currentQuestion.answer_text,
        question_type: currentQuestion.question_type || 'short_answer',
        difficulty,
        subject,
        options: currentQuestion.options
      });
    }

    // If we still don't have questions, create some basic ones from the content
    if (questions.length === 0) {
      console.log('Creating fallback questions from content...');

      const words = text.split(' ').filter((word) => word.length > 3);
      const keyTerms = words.slice(0, 20).join(' ');

      questions.push({
        question_text: `What are the main concepts discussed in the content about ${subject}?`,
        answer_text: `The main concepts include: ${keyTerms}`,
        question_type: 'short_answer',
        difficulty,
        subject
      });

      if (text.length > 200) {
        questions.push({
          question_text: `True or False: The content provides information relevant to ${subject}.`,
          answer_text: 'True',
          question_type: 'true_false',
          difficulty,
          subject,
          options: ['True', 'False']
        });
      }
    }

    console.log(`Manual parsing generated ${questions.length} questions`);
    return questions;
  }

  /**
   * Validate and normalize question type
   */
  private validateQuestionType(type: string): 'multiple_choice' | 'true_false' | 'short_answer' | 'essay' {
    const validTypes = ['multiple_choice', 'true_false', 'short_answer', 'essay'];
    return validTypes.includes(type) ? type as any : 'short_answer';
  }

  /**
   * Process uploaded file and extract questions
   */
  async processFile(
  file: File,
  subject: string,
  difficulty: 'easy' | 'medium' | 'hard' = 'medium')
  : Promise<OCRResult> {
    try {
      console.log('Processing file:', file.name, 'Type:', file.type);

      // Validate file type
      const isPDF = file.type === 'application/pdf';
      const isImage = file.type.startsWith('image/');

      if (!isPDF && !isImage) {
        throw new Error('Please upload a PDF file or an image (PNG, JPG, JPEG).');
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB.');
      }

      let extractedText: string;
      let sourceType: 'pdf' | 'image';

      if (isPDF) {
        extractedText = await this.extractTextFromPDF(file);
        sourceType = 'pdf';
      } else {
        extractedText = await this.extractTextFromImage(file);
        sourceType = 'image';
      }

      if (!extractedText || extractedText.length < 20) {
        throw new Error('Could not extract sufficient text from the file. Please ensure the content is clear and readable.');
      }

      const questions = await this.parseQuestionsFromText(extractedText, subject, difficulty);

      if (questions.length === 0) {
        throw new Error('No questions could be generated from the extracted content.');
      }

      return {
        extractedText,
        questions,
        sourceType,
        fileName: file.name,
        subject
      };

    } catch (error) {
      console.error('Error processing file:', error);
      throw error;
    }
  }

  /**
   * Process web link and extract questions with enhanced error handling
   */
  async processWebLink(
  url: string,
  subject: string,
  difficulty: 'easy' | 'medium' | 'hard' = 'medium')
  : Promise<OCRResult> {
    try {
      console.log('Processing web link:', url);

      // Validate URL format
      try {
        new URL(url);
      } catch {
        throw new Error('Please enter a valid URL (including http:// or https://).');
      }

      console.log('Extracting content from web link...');
      const extractedText = await this.extractTextFromWebLink(url);

      if (!extractedText || extractedText.length < 50) {
        throw new Error('Could not extract sufficient educational content from the web page. The page may be inaccessible, contain mostly media content, or require authentication. Please try a different URL with accessible text content.');
      }

      console.log('Generating questions from extracted content...');
      const questions = await this.parseQuestionsFromText(extractedText, subject, difficulty);

      if (questions.length === 0) {
        throw new Error('No questions could be generated from the web content. The extracted content may not contain sufficient educational material for quiz generation.');
      }

      return {
        extractedText,
        questions,
        sourceType: 'weblink',
        fileName: url,
        subject
      };

    } catch (error) {
      console.error('Error processing web link:', error);

      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('AI service')) {
          throw new Error('AI service is currently unavailable. Please try again in a few moments or contact your administrator if the problem persists.');
        } else if (error.message.includes('Failed to extract content')) {
          throw new Error('Unable to access the webpage content. Please ensure the URL is publicly accessible and contains text content suitable for educational purposes.');
        } else {
          throw error;
        }
      }

      throw new Error('An unexpected error occurred while processing the web link. Please try a different URL or contact support if the issue persists.');
    }
  }
}

export const ocrService = new OCRService();